/**
 * CareVault Theme System
 * Centralized color management and theme configuration
 */

// Base Color Tokens - Raw color values in HSL format
export const colorTokens = {
  // Brand colors - CareVault identity
  brand: {
    primary: '345 67% 55%',      // Pink #D64463
    secondary: '291 64% 42%',    // Purple #9C27B0  
    tertiary: '40 78% 49%',      // Amber #DEA319
  },
  
  // Neutral colors - Base UI colors
  neutral: {
    50: '220 43% 98%',   // Very light blue-gray
    100: '220 43% 97%',  // Light blue-gray #F0F4FA
    200: '220 13% 91%',  // Light gray
    300: '215 16% 79%',  // Medium-light gray
    400: '215 16% 65%',  // Medium gray
    500: '215 16% 47%',  // Medium-dark gray #64748B
    600: '215 19% 35%',  // Dark gray
    700: '210 24% 26%',  // Very dark gray
    800: '210 24% 16%',  // Dark blue-gray #1F303B
    900: '210 24% 12%',  // Very dark blue-gray
    950: '210 24% 8%',   // Almost black
  },
  
  // Status colors
  status: {
    success: '142 76% 36%',    // Green
    warning: '40 78% 49%',     // Amber (same as tertiary)
    error: '0 84% 60%',        // Red
    info: '221 83% 53%',       // Blue
  },
  
  // Extended palette for charts and accents
  extended: {
    pink: {
      light: '345 67% 65%',    // Lighter pink for dark mode
      dark: '345 67% 45%',     // Darker pink
    },
    purple: {
      light: '291 64% 52%',    // Lighter purple
      dark: '291 64% 32%',     // Darker purple
    },
    amber: {
      light: '40 78% 59%',     // Lighter amber
      dark: '40 78% 39%',      // Darker amber
    }
  }
} as const

// Semantic Color Tokens - Meaning-based color assignments
export interface ColorTokens {
  // Brand colors
  brand: {
    primary: string
    secondary: string
    tertiary: string
  }
  
  // Background colors
  background: {
    primary: string     // Main page background
    secondary: string   // Card/component background
    elevated: string    // Modal/dropdown background
    inverse: string     // Opposite theme background
  }
  
  // Text colors
  text: {
    primary: string     // Main text
    secondary: string   // Secondary text
    tertiary: string    // Muted text
    inverse: string     // Text on dark backgrounds
    accent: string      // Accent/link text
  }
  
  // Interactive element colors
  interactive: {
    default: string     // Default interactive state
    hover: string       // Hover state
    active: string      // Active/pressed state
    disabled: string    // Disabled state
  }
  
  // UI element colors
  border: string
  input: string
  ring: string         // Focus ring
  
  // Status colors
  status: {
    success: string
    warning: string
    error: string
    info: string
  }
  
  // Chart colors
  chart: {
    1: string
    2: string
    3: string
    4: string
    5: string
  }
  
  // Glass morphism
  glass: {
    background: string
    border: string
  }
}

// Component-specific tokens
export interface ComponentTokens {
  navigation: {
    background: string
    foreground: string
    accent: string
  }
  
  card: {
    background: string
    border: string
    shadow: string
  }
  
  button: {
    primary: {
      background: string
      foreground: string
      border: string
      hover: string
    }
    secondary: {
      background: string
      foreground: string
      border: string
      hover: string
    }
    ghost: {
      background: string
      foreground: string
      border: string
      hover: string
    }
  }
  
  stats: {
    background: string
    border: string
    accent: string
  }
}

// Theme definition interface
export interface Theme {
  id: string
  name: string
  type: 'light' | 'dark'
  colors: ColorTokens
  components: ComponentTokens
}

// Light theme configuration
export const lightTheme: Theme = {
  id: 'carevault-light',
  name: 'CareVault Light',
  type: 'light',
  colors: {
    brand: {
      primary: colorTokens.brand.primary,
      secondary: colorTokens.brand.secondary,
      tertiary: colorTokens.brand.tertiary,
    },
    background: {
      primary: colorTokens.neutral[100],    // #F0F4FA
      secondary: '0 0% 100%',               // White
      elevated: '0 0% 100%',                // White
      inverse: colorTokens.neutral[800],    // Dark for contrast
    },
    text: {
      primary: colorTokens.neutral[800],    // Dark blue-gray
      secondary: colorTokens.neutral[500],  // Medium gray
      tertiary: colorTokens.neutral[400],   // Light gray
      inverse: '0 0% 98%',                  // Near white
      accent: colorTokens.brand.primary,    // Pink
    },
    interactive: {
      default: colorTokens.brand.primary,
      hover: colorTokens.extended.pink.dark,
      active: colorTokens.extended.pink.dark,
      disabled: colorTokens.neutral[300],
    },
    border: colorTokens.neutral[200],
    input: colorTokens.neutral[200],
    ring: colorTokens.brand.primary,
    status: {
      success: colorTokens.status.success,
      warning: colorTokens.status.warning,
      error: colorTokens.status.error,
      info: colorTokens.status.info,
    },
    chart: {
      1: colorTokens.brand.primary,         // Pink
      2: colorTokens.brand.secondary,       // Purple
      3: colorTokens.brand.tertiary,        // Amber
      4: '320 65% 52%',                     // Pink-Purple blend
      5: '50 85% 55%',                      // Light amber
    },
    glass: {
      background: '0 0% 100% / 0.8',        // White with opacity
      border: '220 13% 91% / 0.2',          // Light border with opacity
    }
  },
  components: {
    navigation: {
      background: colorTokens.brand.primary,
      foreground: '0 0% 100%',
      accent: colorTokens.brand.tertiary,
    },
    card: {
      background: '0 0% 100%',
      border: colorTokens.neutral[200],
      shadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
    },
    button: {
      primary: {
        background: colorTokens.brand.primary,
        foreground: '0 0% 100%',
        border: colorTokens.brand.primary,
        hover: colorTokens.extended.pink.dark,
      },
      secondary: {
        background: colorTokens.neutral[200],
        foreground: colorTokens.neutral[800],
        border: colorTokens.neutral[200],
        hover: colorTokens.neutral[300],
      },
      ghost: {
        background: 'transparent',
        foreground: colorTokens.neutral[800],
        border: 'transparent',
        hover: colorTokens.neutral[100],
      }
    },
    stats: {
      background: '0 0% 100%',
      border: colorTokens.neutral[200],
      accent: colorTokens.brand.primary,
    }
  }
}

// Dark theme configuration  
export const darkTheme: Theme = {
  id: 'carevault-dark',
  name: 'CareVault Dark',
  type: 'dark',
  colors: {
    brand: {
      primary: colorTokens.extended.pink.light,    // Lighter pink for dark mode
      secondary: colorTokens.extended.purple.light, // Lighter purple
      tertiary: colorTokens.extended.amber.light,   // Lighter amber
    },
    background: {
      primary: colorTokens.neutral[800],     // Dark blue-gray
      secondary: colorTokens.neutral[700],   // Slightly lighter
      elevated: colorTokens.neutral[600],    // Card background
      inverse: colorTokens.neutral[100],     // Light for contrast
    },
    text: {
      primary: '0 0% 98%',                   // Near white
      secondary: colorTokens.neutral[300],   // Light gray
      tertiary: colorTokens.neutral[400],    // Medium gray
      inverse: colorTokens.neutral[800],     // Dark
      accent: colorTokens.extended.pink.light, // Light pink
    },
    interactive: {
      default: colorTokens.extended.pink.light,
      hover: colorTokens.brand.primary,
      active: colorTokens.brand.primary,
      disabled: colorTokens.neutral[600],
    },
    border: colorTokens.neutral[600],
    input: colorTokens.neutral[600],
    ring: colorTokens.extended.pink.light,
    status: {
      success: '142 70% 45%',               // Adjusted for dark mode
      warning: colorTokens.extended.amber.light,
      error: '0 62% 50%',                   // Adjusted red
      info: '221 83% 63%',                  // Adjusted blue
    },
    chart: {
      1: colorTokens.extended.pink.light,   // Light pink
      2: colorTokens.extended.purple.light, // Light purple
      3: colorTokens.extended.amber.light,  // Light amber
      4: '320 65% 62%',                     // Light pink-purple blend
      5: '50 85% 65%',                      // Very light amber
    },
    glass: {
      background: '210 20% 20% / 0.8',      // Dark with opacity
      border: '210 15% 25% / 0.2',          // Dark border with opacity
    }
  },
  components: {
    navigation: {
      background: colorTokens.extended.pink.light,
      foreground: '0 0% 100%',
      accent: colorTokens.extended.amber.light,
    },
    card: {
      background: colorTokens.neutral[700],
      border: colorTokens.neutral[600],
      shadow: '0 1px 3px 0 rgb(0 0 0 / 0.3)',
    },
    button: {
      primary: {
        background: colorTokens.extended.pink.light,
        foreground: '0 0% 100%',
        border: colorTokens.extended.pink.light,
        hover: colorTokens.brand.primary,
      },
      secondary: {
        background: colorTokens.neutral[600],
        foreground: '0 0% 98%',
        border: colorTokens.neutral[600],
        hover: colorTokens.neutral[500],
      },
      ghost: {
        background: 'transparent',
        foreground: '0 0% 98%',
        border: 'transparent',
        hover: colorTokens.neutral[700],
      }
    },
    stats: {
      background: colorTokens.neutral[700],
      border: colorTokens.neutral[600],
      accent: colorTokens.extended.pink.light,
    }
  }
}

// Available themes
export const themes = {
  light: lightTheme,
  dark: darkTheme,
} as const

// Theme utilities
export type ThemeId = keyof typeof themes
export type ThemeType = 'light' | 'dark' | 'system'

// Helper function to get theme by ID
export function getTheme(id: ThemeId): Theme {
  return themes[id]
}

// Helper function to resolve system theme
export function resolveSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

// CSS variable generator
export function generateCSSVariables(theme: Theme): Record<string, string> {
  const variables: Record<string, string> = {}
  
  // Brand colors
  variables['--cv-brand-primary'] = theme.colors.brand.primary
  variables['--cv-brand-secondary'] = theme.colors.brand.secondary
  variables['--cv-brand-tertiary'] = theme.colors.brand.tertiary
  
  // Background colors
  variables['--cv-bg-primary'] = theme.colors.background.primary
  variables['--cv-bg-secondary'] = theme.colors.background.secondary
  variables['--cv-bg-elevated'] = theme.colors.background.elevated
  variables['--cv-bg-inverse'] = theme.colors.background.inverse
  
  // Text colors
  variables['--cv-text-primary'] = theme.colors.text.primary
  variables['--cv-text-secondary'] = theme.colors.text.secondary
  variables['--cv-text-tertiary'] = theme.colors.text.tertiary
  variables['--cv-text-inverse'] = theme.colors.text.inverse
  variables['--cv-text-accent'] = theme.colors.text.accent
  
  // Interactive colors
  variables['--cv-interactive-default'] = theme.colors.interactive.default
  variables['--cv-interactive-hover'] = theme.colors.interactive.hover
  variables['--cv-interactive-active'] = theme.colors.interactive.active
  variables['--cv-interactive-disabled'] = theme.colors.interactive.disabled
  
  // UI colors
  variables['--cv-border'] = theme.colors.border
  variables['--cv-input'] = theme.colors.input
  variables['--cv-ring'] = theme.colors.ring
  
  // Status colors
  variables['--cv-status-success'] = theme.colors.status.success
  variables['--cv-status-warning'] = theme.colors.status.warning
  variables['--cv-status-error'] = theme.colors.status.error
  variables['--cv-status-info'] = theme.colors.status.info
  
  // Chart colors
  variables['--cv-chart-1'] = theme.colors.chart[1]
  variables['--cv-chart-2'] = theme.colors.chart[2]
  variables['--cv-chart-3'] = theme.colors.chart[3]
  variables['--cv-chart-4'] = theme.colors.chart[4]
  variables['--cv-chart-5'] = theme.colors.chart[5]
  
  // Glass morphism
  variables['--cv-glass-bg'] = theme.colors.glass.background
  variables['--cv-glass-border'] = theme.colors.glass.border
  
  // Component tokens
  variables['--cv-nav-bg'] = theme.components.navigation.background
  variables['--cv-nav-fg'] = theme.components.navigation.foreground
  variables['--cv-nav-accent'] = theme.components.navigation.accent
  
  variables['--cv-card-bg'] = theme.components.card.background
  variables['--cv-card-border'] = theme.components.card.border
  
  variables['--cv-btn-primary-bg'] = theme.components.button.primary.background
  variables['--cv-btn-primary-fg'] = theme.components.button.primary.foreground
  variables['--cv-btn-primary-hover'] = theme.components.button.primary.hover
  
  variables['--cv-stats-bg'] = theme.components.stats.background
  variables['--cv-stats-border'] = theme.components.stats.border
  variables['--cv-stats-accent'] = theme.components.stats.accent
  
  return variables
}

// Migration mapping for existing hardcoded colors
export const colorMigrationMap = {
  '#D64463': 'brand-primary',
  '#9C27B0': 'brand-secondary', 
  '#DEA319': 'brand-tertiary',
  '#F0F4FA': 'bg-primary',
  '#1F303B': 'text-primary',
  '#64748B': 'text-secondary',
} as const

export type ColorMigrationKey = keyof typeof colorMigrationMap